"use client";

import { useRouter } from "next/navigation";
import { authClient } from "@/lib/auth/auth-client";
import { NavbarTwoRow } from "@/components/layout/navbar-two-row";
import type { Session, User } from "@/lib/auth/auth";
import { useEffect, useState } from "react";
import { useWalletStore } from "@/store/wallet-store";

interface NavbarTwoRowWithAuthProps {
  initialSession: {
    session: Session;
    user: User;
  } | null;
  logoHref?: string;
  showWallet?: boolean;
  /** Safe mode (server-decided): machines shown in the account panel are simulated. */
  testMode?: boolean;
}

export function NavbarTwoRowWithAuth({
  initialSession,
  logoHref = "/",
  showWallet = true,
  testMode = false,
}: NavbarTwoRowWithAuthProps) {
  const router = useRouter();

  // Use reactive session hook for real-time updates
  const { data: clientSession, isPending } = authClient.useSession();

  // Get wallet balance from store
  const walletBalance = useWalletStore((state) => state.balance);
  const walletCurrency = useWalletStore((state) => state.currency);
  const refreshBalance = useWalletStore((state) => state.refreshBalance);
  // The store starts at a placeholder zero; show the balance only once fetched.
  const [walletReady, setWalletReady] = useState(false);

  // Once client session is loaded, always use it
  const session = isPending ? initialSession : clientSession;

  useEffect(() => {
    // Fetch wallet balance if user is authenticated
    if (!showWallet || !session?.user) return;
    let cancelled = false;
    refreshBalance().finally(() => {
      if (!cancelled) setWalletReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, [session?.user, showWallet, refreshBalance]);

  const handleSignOut = async () => {
    await authClient.signOut({
      fetchOptions: {
        onSuccess: () => {
          router.push("/");
        },
      },
    });
  };

  return (
    <NavbarTwoRow
      logoHref={logoHref}
      showWallet={showWallet && !!session?.user}
      walletBalance={walletBalance}
      walletCurrency={walletCurrency}
      walletReady={walletReady}
      walletHref="/wallet"
      showAuth
      isAuthenticated={!!session?.user}
      user={
        session?.user
          ? {
              name: session.user.name,
              email: session.user.email,
              image: session.user.image || undefined,
            }
          : undefined
      }
      testMode={testMode}
      // Fresh balance whenever the account panel opens.
      onAccountOpen={() => {
        if (showWallet) refreshBalance();
      }}
      onSignOut={handleSignOut}
      signInHref="/sign-in"
    />
  );
}
